import type { PayloadAction } from '@reduxjs/toolkit';
import { createAction, createSlice } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import type { Project } from '../../types/project';
import type {
    ActiveCountResponse,
    ObjectType,
    ProjectFile,
    ProjectsMainSearchData,
    ProjectsState,
    ProjectsTasksBadges,
    Tag,
} from './types';

const initialState: ProjectsState = {
    activityProjectsMapping: [],
    tags: {
        all: [],
        active: [],
        projectsTags: {},
        isUpdating: false,
        projectsErrors: {},
    },
    badges: null,
    activeCount: null,
    projectsList: {
        list: [],
        hasMore: false,
        page: 1,
    },
    isUpdating: false,
    hasError: false,
    searchSuggestions: {
        isLoading: false,
        results: {},
    },
    filenameError: {
        code: null,
        id: null,
        name: null,
        message: null,
    },
    tasksBadge: 0,
};

const projectsSlice = createSlice({
    name: 'projects',
    initialState,
    reducers: {
        setTagsToFilter: (state, { payload }: PayloadAction<string[]>) => {
            state.tags.active = payload;
        },
        updateAllProjectsTagSuccess: (state, { payload }: PayloadAction<string[]>) => {
            state.tags.all = payload;
        },
        setProjectsTagsAreUpdating: (state, { payload }: PayloadAction<boolean>) => {
            state.tags.isUpdating = payload;
            state.tags.projectsErrors = {};
        },
        updateProjectTagsSuccess: (state, { payload }: PayloadAction<{ projectId: string; hashes: Tag[] }>) => {
            state.tags.projectsTags[payload.projectId] = payload.hashes;
            state.tags.isUpdating = false;
        },
        updateProjectTagsError: (state, { payload }: PayloadAction<string>) => {
            state.tags.isUpdating = false;
            state.tags.projectsErrors[payload] = true;
        },
        setProjectListIsUpdating: (state, { payload }: PayloadAction<boolean>) => {
            state.isUpdating = payload;
        },
        getProjectList: (state) => {
            state.hasError = false;
        },
        getProjectListSuccess: (
            state,
            {
                payload: { list, badges, projectsTags, hasMoreProjects },
            }: PayloadAction<{
                list: Project[];
                projectsTags: { [id: string]: Tag[] };
                badges: ProjectsTasksBadges;
                hasMoreProjects: boolean;
            }>,
        ) => {
            state.projectsList = {
                list: list || [],
                hasMore: hasMoreProjects,
                page: 1,
            };
            state.badges = castDraft(badges);
            state.tags.projectsTags = projectsTags;
            state.hasError = false;
        },
        getProjectListError: (state) => {
            state.hasError = true;
        },
        updateProjectList: (state, { payload }: PayloadAction<Project[]>) => {
            payload.forEach((project) => {
                const updated = state.projectsList.list.findIndex((item) => item.id === project.id);
                state.projectsList.list[updated] = project;
            });
        },
        loadMoreProjects: (state) => {
            state.hasError = false;
        },
        loadMoreProjectsSuccess: (
            state,
            {
                payload: { list, badges, projectsTags, hasMoreProjects },
            }: PayloadAction<{
                list: Project[];
                projectsTags: { [id: string]: Tag[] };
                badges: ProjectsTasksBadges;
                hasMoreProjects: boolean;
            }>,
        ) => {
            state.projectsList = {
                list: [...state.projectsList.list, ...list],
                hasMore: hasMoreProjects,
                page: state.projectsList.page + 1,
            };
            state.badges = castDraft(badges);
            state.tags.projectsTags = projectsTags;
        },
        loadMoreProjectsError: (state) => {
            state.hasError = true;
        },
        projectsExpandAll: (state) => {
            state.projectsList.list.forEach((project) => {
                // eslint-disable-next-line no-param-reassign
                project.isCollapsed = false;
            });
        },
        projectsCollapseAll: (state) => {
            state.projectsList.list.forEach((project) => {
                // eslint-disable-next-line no-param-reassign
                project.isCollapsed = true;
            });
        },
        switchProjectView: (state, { payload }: PayloadAction<{ projectId: string; collapsed: boolean }>) => {
            state.projectsList.list.forEach((project) => {
                if (project.id === payload.projectId) {
                    // eslint-disable-next-line no-param-reassign
                    project.isCollapsed = payload.collapsed;
                }
            });
        },
        setProjectsListOrder: (state, { payload }: PayloadAction<Project[]>) => {
            state.projectsList.list = payload;
        },
        getProjectsMainSearch: (
            state,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            action: PayloadAction<{ query: string; objectType: ObjectType; exclude: boolean }>,
        ) => {
            state.searchSuggestions.isLoading = true;
        },
        getProjectsMainSearchError: (state) => {
            state.searchSuggestions.isLoading = false;
        },
        getProjectsMainSearchSuccess: (
            state,
            {
                payload,
            }: PayloadAction<{ query: string; objectType: ObjectType; searchResults: ProjectsMainSearchData }>,
        ) => {
            state.searchSuggestions.isLoading = false;
            state.searchSuggestions.results = {
                ...state.searchSuggestions.results,
                [payload.objectType]: {
                    ...state.searchSuggestions.results[payload.objectType],
                    [payload.query]: payload.searchResults,
                },
            };
        },
        renameProjectFilenameSuccess: (
            state,
            { payload }: PayloadAction<{ projectId: string; filenameId: string; newFilename: string }>,
        ) => {
            const mapFilename = (file: ProjectFile) =>
                file.id === payload.filenameId ? { ...file, name: payload.newFilename } : file;

            const mapProject = (project: Project) =>
                project.id === payload.projectId
                    ? {
                          ...project,
                          filenames: project.filenames.map(mapFilename),
                      }
                    : project;

            state.projectsList.list = state.projectsList.list.map(mapProject);
        },
        renameProjectFilenameError: (
            state,
            {
                payload,
            }: PayloadAction<{
                projectId: string | null;
                filenameId: string | null;
                newFilename: string | null;
                error: 1 | 2 | null;
                message?: string;
            }>,
        ) => {
            state.filenameError = {
                code: payload.error,
                id: payload.filenameId,
                name: payload.newFilename,
                message: payload.message ?? null,
            };
        },
        setBadgesData: (state, { payload }: PayloadAction<ProjectsTasksBadges>) => {
            state.badges = castDraft(payload);
        },
        setTaskBadge: (state, { payload }: PayloadAction<number>) => {
            state.tasksBadge = payload;
        },
        setActiveCount: (state, { payload }: PayloadAction<ActiveCountResponse>) => {
            state.activeCount = payload;
        },
    },
});

const updateProjectTags = createAction('project/updateProjectTags', (projectId: string, tags: string[]) => ({
    payload: { projectId, tags },
}));

const setProjectActiveBranch = createAction('project/setProjectActiveBranch', (projectId: string, branch: string) => ({
    payload: { projectId, branch },
}));

const setProjectActiveBranchSuccess = createAction('projects/setProjectActiveBranchSuccess');

const renameProjectFilename = createAction(
    'project/renameProjectFilename',
    (projectId: string, filenameId: string, newFilename: string, force?: boolean) => ({
        payload: {
            projectId,
            filenameId,
            newFilename,
            force,
        },
    }),
);

const initProjectsList = createAction('projects/initProjectsList');

export default projectsSlice.reducer;

export const projectsActions = {
    ...projectsSlice.actions,
    updateProjectTags,
    setProjectActiveBranch,
    setProjectActiveBranchSuccess,
    renameProjectFilename,
    initProjectsList,
};
